import React from 'react';
import { CardContent, Stack, Alert, AlertTitle } from '@mui/material';
import { useAppSelector } from '../../../../app/hooks';
import CategoryClassificationSection from './sections/CategoryClassificationSection';
import TicketInformationSection from './sections/TicketInformationSection';
import TimeTrackingSection from './sections/TimeTrackingSection';
import CustomerInformationSection from './sections/CustomerInformationSection';
import { HstAccessSection } from './sections/HstAccessSection';
import { CpsiAccessSection } from './sections/CpsiAccessSection';
import { FacilityMaintenanceSection } from './sections/FacilityMaintenanceSection';
import { CustomApplicationSection } from './sections/CustomApplicationSection';
import { ReportingSection } from './sections/ReportingSection';

/**
 * TicketCreateForm - Main form container for ticket creation
 * Organizes all sections in a logical flow and shows conditional sections based on category
 */
export const TicketCreateForm: React.FC = () => {
  const currentTicket = useAppSelector(state => state.ticket.currentTicket);
  
  // Determine which main content section to show based on category
  const showTicketInformation = currentTicket?.category === 'GEN' || !currentTicket?.category;
  const showHstAccessSection = currentTicket?.category === 'HST';
  const showCpsiAccessSection = currentTicket?.category === 'CPSI';
  const showFacilityMaintenanceSection = currentTicket?.category === 'FAC';
  const showCustomApplicationSection = currentTicket?.category === 'APP';
  const showReportingSection = currentTicket?.category === 'RPT';
  
  return (
    <CardContent sx={{ p: 3, backgroundColor: '#ffffff' }}>
      <form autoComplete="off">
        <Stack spacing={2.5}>
          <CategoryClassificationSection />
          
          {/* Conditional Main Content Section */}
          {showTicketInformation && <TicketInformationSection />}
          {showHstAccessSection && <HstAccessSection />}
          {showCpsiAccessSection && <CpsiAccessSection />}
          {showFacilityMaintenanceSection && <FacilityMaintenanceSection mode="ticket" />}
          {showCustomApplicationSection && <CustomApplicationSection mode="ticket" />}
          {showReportingSection && <ReportingSection mode="ticket" />}
          
          <TimeTrackingSection />
          <CustomerInformationSection />
          
          {/* Alert about documents and notes */}
          <Alert 
            severity="info" 
            sx={{ 
              backgroundColor: '#f0f9ff',
              border: '1px solid #bae6fd',
              borderRadius: 1.5,
              '& .MuiAlert-icon': {
                color: '#0284c7'
              }
            }}
          >
            <AlertTitle sx={{ fontWeight: 600, color: '#0f172a' }}>
              Documents & Notes
            </AlertTitle>
            Add documents and notes after saving the ticket.
          </Alert>
        </Stack>
      </form>
    </CardContent>
  );
};

export default TicketCreateForm;
